// The microphone: one getUserMedia stream and one AudioContext shared
// by the capture and the detector, as the audio-chat pre-image taught
// (moonshine #3904): the Silero VAD (vad-web, the orb's model, served
// from our own origin) is only the detector — the audio that goes to
// the socket is kept by the page's own AudioWorklet at the device's
// rate. A 0.4 s pre-roll ring covers the detector's late word so no
// turn loses its first consonant; 250 ms of speech is already a turn,
// so "joo" and "ei" survive; the redemption silence is the turn-ending
// setting.
import { batcher } from "@/audio/pcm";
import type { TurnEv } from "@/talk/turns";

// The worklet that hands every 128-sample block of channel 0 to the
// page. It writes nothing to the output: a node that leads nowhere
// may not be run, so it is connected on to the destination, which
// stays silent.
const LISTENER = URL.createObjectURL(
  new Blob(
    [
      `registerProcessor("listener", class extends AudioWorkletProcessor {
        process([input]) { if (input[0]) this.port.postMessage(input[0].slice()); return true; }
      })`,
    ],
    { type: "text/javascript" },
  ),
);

// Seconds of sound kept from before the detector says speech began:
// it says so a frame or two late.
const PREROLL = 0.4;

export type Hearing = "opening" | "quiet" | "speech";

export interface MicOptions {
  // The silence that ends a turn, ms (the detector's redemption).
  silenceMs: number;
  // The detector's positive speech threshold.
  threshold: number;
  // The machine's events, in order.
  ev: (e: TurnEv) => void;
  hearing: (h: Hearing) => void;
}

export interface MicHandle {
  rate: number;
  // Push-to-talk: the key, not the detector, says when speech begins
  // and ends. While held, the detector's word is ignored.
  press(): void;
  release(): void;
  // The turn ended under the mic's feet (the machine reset: an error,
  // the stop button, a dropped socket): stop taking this speech and
  // drop its partial batch, quietly — no event, the machine already
  // knows. The detector goes on; its own end of this speech lands as a
  // no-op.
  interrupt(): void;
  close(): void;
}

export async function openMic(opts: MicOptions): Promise<MicHandle> {
  const { MicVAD } = await import("@ricky0123/vad-web");
  const stream = await navigator.mediaDevices.getUserMedia({
    // The pre-image's finding: echo cancellation on, the rest off —
    // all three together mangle what is said over the agent's speech.
    audio: { echoCancellation: true, noiseSuppression: false, autoGainControl: false },
  });
  const undo: Array<() => void> = [];
  undo.push(() => stream.getTracks().forEach((t) => t.stop()));
  let closed = false;
  let held = false; // push-to-talk holds the turn open
  let taking = false; // blocks go to the machine
  const ctx = new AudioContext();
  undo.push(() => {
    if (ctx.state !== "closed") void ctx.close();
  });
  await ctx.audioWorklet.addModule(LISTENER);
  const ring: Float32Array[] = [];
  const batches = batcher(ctx.sampleRate, 100);
  const take = (block: Float32Array): void => {
    const out = batches.push(block);
    if (out) opts.ev({ t: "block", pcm: out });
  };
  const listener = new AudioWorkletNode(ctx, "listener");
  listener.port.onmessage = (e: MessageEvent<Float32Array>) => {
    const block = e.data;
    ring.push(block);
    const kept = (PREROLL * ctx.sampleRate) / block.length;
    if (!taking) while (ring.length > kept) ring.shift();
    if (taking) take(block);
  };
  ctx.createMediaStreamSource(stream).connect(listener).connect(ctx.destination);

  const startSpeech = () => {
    taking = true;
    opts.hearing("speech");
    // The pre-roll goes first, so the turn's first consonant is in.
    opts.ev({ t: "speechStart", rate: ctx.sampleRate });
    for (const b of ring) take(b);
    ring.length = 0;
  };
  const endSpeech = () => {
    taking = false;
    opts.hearing("quiet");
    // A half-spoken batch waits in the batcher; the turn's end must
    // carry it, or its last word loses its tail.
    const last = batches.flush();
    if (last) opts.ev({ t: "block", pcm: last });
    opts.ev({ t: "speechEnd" });
  };

  const vad = await MicVAD.new({
    model: "v5",
    audioContext: ctx,
    // The stream is ours: the detector is given it and never closes it.
    getStream: async () => stream,
    pauseStream: async () => {},
    resumeStream: async () => stream,
    // Served from our origin (public/vad, copied from node_modules at
    // build time), not a CDN.
    baseAssetPath: "/vad/",
    onnxWASMBasePath: "/vad/ort/",
    positiveSpeechThreshold: opts.threshold,
    negativeSpeechThreshold: Math.max(0, opts.threshold - 0.15),
    redemptionMs: opts.silenceMs,
    minSpeechMs: 250,
    onSpeechStart: () => {
      if (closed || held) return;
      startSpeech();
    },
    onVADMisfire: () => {
      if (closed || held) return;
      taking = false;
      // The partial batch of the misfired sound goes with it, or it
      // opens the next turn as its first audio.
      batches.drop();
      opts.hearing("quiet");
      opts.ev({ t: "misfire" });
    },
    onSpeechEnd: () => {
      if (closed || held) return;
      endSpeech();
    },
  });
  undo.push(() => vad.destroy());
  await vad.start();
  if (closed) {
    undo.forEach((f) => f());
    throw new Error("the microphone was closed before it opened");
  }
  opts.hearing("quiet");

  return {
    rate: ctx.sampleRate,
    press() {
      if (closed || taking) return;
      held = true;
      startSpeech();
    },
    release() {
      if (!held) return;
      held = false;
      endSpeech();
    },
    interrupt() {
      held = false;
      if (!taking) return;
      taking = false;
      batches.drop();
      opts.hearing("quiet");
    },
    close() {
      if (closed) return;
      closed = true;
      held = false;
      taking = false;
      undo.forEach((f) => f());
    },
  };
}
